import { flow, Instance, types } from 'mobx-state-tree';
import { FieldState, FormState } from 'formstate';
import { getFormValues } from '@yl/react-forms';

import { required } from '../../customer-information/shared/validators/RequiredValidator';
import { maxLength } from '../../customer-information/shared/validators/MaxLengthValidator';
import { ibanValidator, normalizeIban } from '../components/Sepa/validators/validate-iban';
import { SepaFormModel } from '../../../external/shared/api/EnrollmentClient.generated';

// Worldline rejects sepaDirectDebit.mandate.debtor.firstName over 15 characters
// with 21000220 FIELD_LENGTH_INVALID (ECOM6-1852).
export const sepaFirstNameMaxLength = 15;

export const Sepa = types
  .model({
    displayOnly: types.optional(types.boolean, false),
    firstNameOnAccount: types.maybe(types.string),
    lastNameOnAccount: types.maybe(types.string),
    iban: types.maybe(types.string),
    isDefaultPaymentMethod: types.maybe(types.boolean),
    agreementAccepted: types.optional(types.boolean, false)
  })
  .views(self => ({
    get reactForm() {
      const fields: { [key: string]: FieldState<any> } = {
        firstNameOnAccount: new FieldState(self.firstNameOnAccount || '').validators(
          required(),
          maxLength(sepaFirstNameMaxLength)
        ),
        lastNameOnAccount: new FieldState(self.lastNameOnAccount || '').validators(required()),
        // Auto-validation off: every prefix of an IBAN is invalid, so validating while
        // the member types would show an error for the whole entry. YlInput's onBlur
        // calls enableAutoValidationAndValidate, matching CreditCard's number field.
        iban: new FieldState(self.iban || '').disableAutoValidation().validators(required(), ibanValidator()),
        agreementAccepted: new FieldState(self.agreementAccepted).validators(required())
      };

      return new FormState(fields);
    },
    canProceed() {
      if (self.displayOnly) {
        return true;
      }
      return !this.reactForm.hasError;
    }
  }))
  .actions(self => ({
    prepareResults: flow(function* prepareResults() {
      if (self.displayOnly) {
        return Promise.resolve(true);
      }
      yield self.reactForm.validate();
      return !self.reactForm.hasError;
    }),
    getDataToSubmit() {
      const form = getFormValues(self.reactForm);
      return {
        firstNameOnAccount: form.firstNameOnAccount,
        lastNameOnAccount: form.lastNameOnAccount,
        iban: normalizeIban(form.iban || ''),
        agreementAccepted: form.agreementAccepted
      } as SepaFormModel;
    }
  }));

export type Sepa = Instance<typeof Sepa>;
